// ============================================================
// FILE: @components/confirm-dialog/confirm-dialog.component.ts
// Yes/no confirmation built on ModalShellComponent. Variant 'danger'
// colours the confirm button red -- used for Reject / Cancel / Delete /
// Disconnect-all flows.
//
// Ported from EzovionOPDFrontend (Angular 20) shared/components/
// confirm-dialog/confirm-dialog.component.ts -- standalone component
// converted to a plain NgModule-declared component (registered in
// ComponentsModule alongside ModalShellComponent, which it depends on),
// template `@if` converted to `*ngIf`. OnPush dropped to match this
// target app's typical non-OnPush convention.
//
// CSS custom properties fall back to literal defaults here (via
// `var(--x, default)`) as a defensive measure, but in practice they are
// inherited from ModalShellComponent's own `:host` block: Angular's
// emulated view encapsulation only scopes selectors via attributes, it
// does not create a Shadow DOM boundary, so custom properties still
// cascade normally from `app-modal-shell`'s host element down into this
// component's projected `[body]`/`[footer]` content.
// ============================================================
import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-confirm-dialog',
  template: `
    <app-modal-shell [title]="title" [subtitle]="subtitle" (close)="cancelled.emit()">
      <div body>
        <p class="cd-msg">{{ message }}</p>
        <ng-container *ngIf="showReason">
          <label class="cd-label">{{ reasonLabel }}<span class="cd-req" *ngIf="reasonRequired"> *</span></label>
          <textarea class="cd-textarea" rows="3"
                    [value]="reasonValue"
                    (input)="reasonValue = $any($event.target).value"
                    [placeholder]="reasonPlaceholder"></textarea>
        </ng-container>
      </div>
      <div footer class="cd-footer">
        <button type="button" class="btn btn-ghost" (click)="cancelled.emit()">{{ cancelLabel }}</button>
        <button type="button" class="btn"
                [class.btn-primary]="variant === 'primary'"
                [class.btn-danger]="variant === 'danger'"
                [disabled]="busy || (showReason && reasonRequired && !reasonValue.trim())"
                (click)="onConfirm()">
          {{ busy ? busyLabel : confirmLabel }}
        </button>
      </div>
    </app-modal-shell>
  `,
  styles: [`
    // Mirrors ModalShellComponent's own .ms-footer gap (see that component's
    // doc comment) -- .ms-footer's gap only takes effect between direct
    // flex children, but content projection places this whole [footer] div
    // as its single child, so without its own display:flex the Cancel/
    // Confirm buttons here fall back to bare inline whitespace instead of a
    // deliberate gap, same as the sibling bug fixed on device-integration's
    // Add Device modal footer (see that component's .di-modal-footer).
    .cd-footer { display: flex; align-items: center; gap: 8px; }
    .cd-msg { font-size: 13px; color: var(--text, #1a1916); line-height: 1.5; margin: 0 0 12px; }
    .cd-label { display: block; font-size: 12px; color: var(--text2, #6b6a65); margin-bottom: 4px; }
    .cd-req { color: #791F1F; }
    .cd-textarea {
      width: 100%; padding: 8px 10px; font: inherit;
      border: 1px solid var(--border, rgba(0,0,0,0.08)); border-radius: var(--radius, 10px); resize: vertical;
    }
    .btn { font-size: 13px; padding: 6px 14px; border-radius: var(--radius, 10px); cursor: pointer; }
    .btn-ghost { background: transparent; border: 1px solid var(--border, rgba(0,0,0,0.08)); color: var(--text, #1a1916); }
    .btn-primary { background: var(--blue, #185FA5); color: #fff; border: 0; }
    .btn-danger  { background: #791F1F; color: #fff; border: 0; }
    .btn[disabled] { opacity: .55; cursor: not-allowed; }
  `],
})
export class ConfirmDialogComponent {
  @Input() title = 'Are you sure?';
  @Input() subtitle?: string;
  @Input() message = '';
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel  = 'Cancel';
  @Input() busyLabel    = 'Working…';
  @Input() variant: 'primary' | 'danger' = 'primary';
  @Input() busy = false;

  @Input() showReason = false;
  @Input() reasonLabel = 'Reason';
  @Input() reasonPlaceholder = '';
  @Input() reasonRequired = false;

  reasonValue = '';

  @Output() cancelled = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<string>();    // emits reasonValue (empty when not collected)

  onConfirm(): void {
    if (this.busy) return;
    if (this.showReason && this.reasonRequired && !this.reasonValue.trim()) return;
    this.confirmed.emit(this.reasonValue);
  }
}
